import API from "../api/API";
import types from "./mutation-types";
import constants from "../constants";

const actions = {
  /**
   * Gets the latest CMS editable page data.
   *
   * @param {Function} commit
   *
   * @return {Promise}
   */
  syncCmsData({ commit }) {
    return new Promise((resolve, reject) => {
      API.fetch("cms")
        .then(response => {
          commit(types.ADD_CMS_DATA, response.data.cms);
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  /**
   * Gets the latest Notification data.
   *
   * @param {Function} commit
   *
   * @return {Promise}
   */
  syncNotificationData({ commit }) {
    return new Promise((resolve, reject) => {
      API.fetch("notification")
        .then(response => {
          commit(types.ADD_NOTIFICATION_DATA, response.data.notification);
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  /**
   * Gets a list of regions from the server and stores them in state.
   *
   * @param {Function} commit
   * @param {String} [id]
   *
   * @return {Promise}
   */
  syncRegions({ commit }, id) {
    return new Promise((resolve, reject) => {
      API.fetch("region", id)
        .then(response => {
          const regions = response.data.regions;

          if (regions === undefined || regions.length === 0) {
            resolve(regions);
            return;
          }

          regions.forEach(region => {
            region.geometry = JSON.parse(region.geometry);
            commit(types.ADD_REGION, region);
          });

          resolve(regions);
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  /**
   * Gets a list of forecasts from the server and stores them in state.
   *
   * @param {Function} commit
   * @param {String} [id]
   *
   * @return {Promise}
   */
  syncForecasts({ commit }, id) {
    return new Promise((resolve, reject) => {
      API.fetch("forecast", id)
        .then(response => {
          const forecasts = response.data.forecasts;

          if (forecasts === undefined || forecasts.length === 0) {
            resolve(forecasts);
            return;
          }

          forecasts.forEach(forecast => {
            commit(types.ADD_FORECAST, forecast);
          });

          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  /**
   * Gets the current reCAPTCHA site key.
   *
   * @param {Function} commit
   *
   * @return {Promise}
   */
  syncRecaptchaKey({ commit }) {
    return new Promise((resolve, reject) => {
      API.fetch("recaptcha")
        .then(response => {
          commit(types.ADD_RECAPTCHA_KEY, response.data.recaptcha);
          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  /**
   * Gets a list of public observations from the server and stores them in state.
   *
   * @param {Function} commit
   *
   * @return {Promise}
   */
  syncObservations({ commit }, payload) {
    let path = "";

    if (payload) {
      if (payload.obs) {
        path = `/${payload.obs}`;
      }

      if (payload.dateFrom) {
        path = `?dateFrom=${payload.dateFrom}`;

        if (payload.dateTo) {
          path += `&dateTo=${payload.dateTo}`;
        }
      }
    }
    return new Promise((resolve, reject) => {
      API.fetch("observation" + path)
        .then(response => {
          const observations = response.data.observations;

          if (observations === undefined || observations.length === 0) {
            resolve();
            return;
          }

          observations.forEach(observation => {
            if (observation.triggerType) {
              observation.triggerType = constants.TRIGGER_TYPES[observation.triggerType] || observation.triggerType;
            }

            commit(types.ADD_OBSERVATION, observation);
          });

          resolve();
        })
        .catch(error => {
          reject(error);
        });
    });
  },

  /**
   * Manually add an observation into the page (e.g. when we have submitted a new observation)
   *
   * @param {Function} commit
   * @param {Object} observation
   *
   * @return {Promise}
   */
  addObservation({ commit }, observation) {
    return new Promise(resolve => {
      commit(types.ADD_OBSERVATION, observation);
      resolve();
    });
  },

  /**
   * Sets the currently selected region.
   *
   * @param {Function} commit
   * @param {Object} region
   *
   * @return {Promise}
   */
  setSelectedRegion({ commit }, region) {
    return new Promise(resolve => {
      commit(types.SET_SELECTED_REGION, region);
      resolve();
    });
  },

  /**
   * Clears selected region.
   *
   * @param {Function} commit
   *
   * @return {Promise}
   */
  clearSelectedRegion({ commit }) {
    return new Promise(resolve => {
      commit(types.CLEAR_SELECTED_REGION);
      resolve();
    });
  },

  /**
   * Sets the currently selected public observation.
   *
   * @param {Function} commit
   * @param {Object} observation
   */
  setSelectedObservation({ commit }, observation) {
    return new Promise(resolve => {
      commit(types.SET_SELECTED_OBSERVATION, observation);
      resolve();
    });
  }
};

export default actions;
